/**
 * The event flow, wired to `POST` / `PATCH` / `DELETE /garages/:id/events`
 * (owner or crew — booking the weekend is a crew action, §2).
 *
 * An event is the spine the whole forecast hangs off: its date is a deadline,
 * and its estimated passes and miles are what the projection engine walks to
 * decide where each service lands (§4). So the two estimates are the point of
 * the form, not an afterthought — a car that "runs 8 passes" is what turns "due
 * in 20 passes" into "due at this event".
 *
 * The event's identity is its **name**, not its track: a touring series runs the
 * same event across several venues, so the track is optional and the name is
 * what everything headlines.
 *
 * Planned events only. Converting one to actual (which advances real usage) is a
 * separate action against a different route.
 */

import { useState } from 'react';
import { CircleCheck, Flag, Gauge, MapPin, Trash2 } from 'lucide-react';

import type { CompleteEventRequest, CreateEventRequest, EventDto, UpdateEventRequest } from '@svcdue/contracts';

import { ApiError } from '../api/client';
import { useCars, useCompleteEvent, useCreateEvent, useDeleteEvent, useDrivers, useUpdateEvent } from '../api/hooks';
import { errorMessage, QueryState } from '../components/states';
import { Field, SheetHead } from '../components/form';
import { today } from '../lib/format';

export interface EventForm {
  carId: string;
  driverId: string;
  date: string;
  track: string;
  name: string;
  estPasses: string;
  estMiles: string;
}

/** The fields shared by create and update. Track is optional — blank means none. */
function toBody(form: EventForm, streetCar: boolean) {
  return {
    driverId: form.driverId || null,
    date: form.date,
    name: form.name.trim(),
    // Blank track → null, so a multi-track event carries no venue rather than "".
    track: form.track.trim() || null,
    estPasses: Number(form.estPasses) || 0,
    // A strip-only car accrues no miles, so its estimate is always zero — the
    // field is hidden for it and this makes sure a stale value never leaks out.
    estMiles: streetCar ? Number(form.estMiles) || 0 : 0,
  };
}

export function toRequest(form: EventForm, streetCar: boolean): CreateEventRequest {
  // carId is only set at creation — an event cannot be moved to another car.
  return { carId: form.carId, ...toBody(form, streetCar) };
}

export function toUpdate(form: EventForm, streetCar: boolean): UpdateEventRequest {
  return toBody(form, streetCar);
}

/** The "record actuals" sub-form. Kept apart from the estimate form because it
 *  drives a different, one-way route (`/actual`) — not a field of the edit. */
export interface ActualForm {
  passes: string;
  miles: string;
}

/**
 * The actuals form → `completeEventRequest`.
 *
 * `actualPasses` is required — the whole point of converting an event is to
 * replace an estimate with a fact, and a conversion carrying no number would
 * silently zero the usage the forecast rests on. Miles follow the same
 * strip-only rule as the estimate: a car that never sees a street accrues none,
 * so its field is hidden and any lingering value is zeroed here rather than
 * leaked into usage. Pure and exported so the mapping is checkable without a sheet.
 */
export function toComplete(form: ActualForm, streetCar: boolean): CompleteEventRequest {
  return {
    actualPasses: Number(form.passes) || 0,
    actualMiles: streetCar ? Number(form.miles) || 0 : 0,
  };
}

function eventToForm(event: EventDto): EventForm {
  return {
    carId: event.carId,
    driverId: event.driverId ?? '',
    date: event.date,
    track: event.track ?? '',
    name: event.name,
    estPasses: event.estPasses.toString(),
    estMiles: event.estMiles ? event.estMiles.toString() : '',
  };
}

export function AddEvent({
  garageId,
  event,
  onClose,
}: {
  garageId: string;
  event?: EventDto;
  onClose: () => void;
}) {
  const cars = useCars(garageId);
  const drivers = useDrivers(garageId);

  // The car list must be in hand before the form is usable — the miles field
  // depends on whether the car is street or strip. Until it loads (or if there
  // is no car), show a sheet with an inert Save; the real header, with a working
  // Save wired to submit, lives inside the form so there is only ever one.
  const ready = cars.isSuccess && cars.data.length > 0;

  if (ready) {
    return (
      <EventFormSheet
        garageId={garageId}
        event={event}
        cars={cars.data}
        drivers={drivers.data ?? []}
        onClose={onClose}
      />
    );
  }

  return (
    <div className="sheet">
      <SheetHead title={event ? 'Edit event' : 'Add event'} onClose={onClose} saveLabel="Save" onSave={() => {}} saveDisabled />
      <div className="sheet-body">
        <QueryState query={cars}>
          {(carList) =>
            carList.length === 0 ? (
              <div className="rule-empty">Add a car first — an event is run by one.</div>
            ) : null
          }
        </QueryState>
      </div>
    </div>
  );
}

function EventFormSheet({
  garageId,
  event,
  cars,
  drivers,
  onClose,
}: {
  garageId: string;
  event?: EventDto;
  cars: { id: string; nickname: string; useType: string }[];
  drivers: { id: string; name: string }[];
  onClose: () => void;
}) {
  const editing = event !== undefined;
  const create = useCreateEvent(garageId);
  const update = useUpdateEvent(garageId, event?.id ?? '');
  const del = useDeleteEvent(garageId);
  const complete = useCompleteEvent(garageId, event?.id ?? '');
  const save = editing ? update : create;

  const [f, setF] = useState<EventForm>(
    event ? eventToForm(event) : { carId: cars[0]!.id, driverId: '', date: today(), track: '', name: '', estPasses: '8', estMiles: '' },
  );
  const set = <K extends keyof EventForm>(k: K, v: EventForm[K]) => setF((s) => ({ ...s, [k]: v }));
  const [confirmDelete, setConfirmDelete] = useState(false);

  // The actuals seed from the estimate — you are correcting a guess to a fact,
  // and the guess is the natural starting point.
  const [actual, setActual] = useState<ActualForm>(
    event ? { passes: event.estPasses.toString(), miles: event.estMiles ? event.estMiles.toString() : '' } : { passes: '', miles: '' },
  );
  const setA = <K extends keyof ActualForm>(k: K, v: ActualForm[K]) => setActual((s) => ({ ...s, [k]: v }));

  const car = cars.find((c) => c.id === f.carId);
  const isStreet = car?.useType === 'street';

  const issue = (path: string) => (save.error instanceof ApiError ? save.error.issueFor(path) : undefined);
  // Only the name is required now — track is optional.
  const valid = Boolean(f.name.trim() && f.date);
  const busy = save.isPending || del.isPending || complete.isPending;

  // An event that has already run carries facts, not estimates — its actuals are
  // read-only, and the record control is gone (the API 409s on a re-complete).
  const isActual = event?.status === 'actual';

  const submit = () => {
    if (!valid || busy) return;
    if (editing) update.mutate(toUpdate(f, isStreet), { onSuccess: onClose });
    else create.mutate(toRequest(f, isStreet), { onSuccess: onClose });
  };
  const remove = () => {
    if (!event || busy) return;
    del.mutate(event.id, { onSuccess: onClose });
  };
  const record = () => {
    // Requires a pass count — the conversion's whole purpose is a real number.
    if (!event || !actual.passes.trim() || busy) return;
    complete.mutate(toComplete(actual, isStreet), { onSuccess: onClose });
  };

  return (
    <div className="sheet">
      <SheetHead
        title={editing ? 'Edit event' : 'Add event'}
        onClose={onClose}
        saveLabel={save.isPending ? 'Saving…' : editing ? 'Save' : 'Add event'}
        onSave={submit}
        saveDisabled={!valid || busy}
      />
      <div className="sheet-body">
        <div className="form-sec">
          <Flag size={12} /> Which car
        </div>
        <Field label="Car">
          {/* An event belongs to its car; moving it is delete-and-recreate, and
              the update route does not accept a car change — so it is read-only
              when editing. */}
          {editing ? (
            <input className="input" value={car?.nickname ?? ''} disabled />
          ) : (
            <select className="select" value={f.carId} onChange={(e) => set('carId', e.target.value)}>
              {cars.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.nickname}
                </option>
              ))}
            </select>
          )}
        </Field>
        {drivers.length > 0 && (
          <Field label="Driver" hint="optional">
            <select className="select" value={f.driverId} onChange={(e) => set('driverId', e.target.value)}>
              <option value="">—</option>
              {drivers.map((d) => (
                <option key={d.id} value={d.id}>
                  {d.name}
                </option>
              ))}
            </select>
          </Field>
        )}

        <div className="form-sec">
          <MapPin size={12} /> Where and when
        </div>
        <Field label="Event name">
          <input className="input" value={f.name} onChange={(e) => set('name', e.target.value)} placeholder="e.g., Summit ET Series" />
          {issue('name') && <div className="field-err">{issue('name')}</div>}
        </Field>
        <Field label="Track" hint="optional — leave blank for a multi-track series">
          <input className="input" value={f.track} onChange={(e) => set('track', e.target.value)} placeholder="e.g., Bradenton Motorsports Park" />
          {issue('track') && <div className="field-err">{issue('track')}</div>}
        </Field>
        <Field label="Date">
          <input className="input" type="date" value={f.date} onChange={(e) => set('date', e.target.value)} />
          {issue('date') && <div className="field-err">{issue('date')}</div>}
          {/* A past date is allowed — you might be recording a weekend that
              already ran — but it will not show on the dashboard as an upcoming
              event, and picking last year by leaving the year alone is an easy
              mistake. So say so rather than let the event silently vanish. */}
          {f.date && f.date < today() && !issue('date') && (
            <div className="field-note">
              This date is in the past — it won't appear as an upcoming event on the dashboard.
            </div>
          )}
        </Field>

        <div className="form-sec">Estimated usage</div>
        <p className="lede" style={{ marginTop: 0 }}>
          Roughly how much this car will run. The forecast walks these numbers to place each service — a
          best guess is enough, and you can log the real figures afterwards.
        </p>
        <div className={isStreet ? 'field-grid-2' : ''}>
          <Field label="Passes">
            <input className="input" value={f.estPasses} onChange={(e) => set('estPasses', e.target.value)} inputMode="numeric" placeholder="8" />
          </Field>
          {/* Miles only matter for a street car; a strip-only car tows in. */}
          {isStreet && (
            <Field label="Miles" hint="street driving to/from">
              <input className="input" value={f.estMiles} onChange={(e) => set('estMiles', e.target.value)} inputMode="numeric" placeholder="40" />
            </Field>
          )}
        </div>

        {/**
          * Recording actuals — reachable only on an event that already exists.
          *
          * This is a different action from saving the estimate, and against a
          * different route: it converts the event to `actual` and advances real
          * usage, moving the whole forecast. So it lives in its own block, is not
          * wired to the header Save, and says plainly that it is not lightly
          * undone. Once run, the event is history and its numbers show read-only.
          */}
        {isActual ? (
          <div className="record-actual record-done">
            <div className="record-h">
              <CircleCheck size={13} /> Recorded — this event has run
            </div>
            <div className="record-facts">
              <div>
                <span>Actual passes</span>
                <b>{event!.actualPasses ?? 0}</b>
              </div>
              {isStreet && (
                <div>
                  <span>Actual miles</span>
                  <b>{event!.actualMiles ?? 0}</b>
                </div>
              )}
            </div>
            <p className="field-note record-note-plain">
              Actuals are locked in — the forecast now counts from these. To change them, undo the completion rather
              than editing underneath it.
            </p>
          </div>
        ) : (
          editing && (
            <div className="record-actual">
              <div className="record-h">
                <Gauge size={13} /> Record what actually ran
              </div>
              <p className="field-note record-note-plain">
                Converts this event to actual and advances real usage — every counter on the car moves. Not undone
                lightly, so log it once the weekend is truly over.
              </p>
              <div className={isStreet ? 'field-grid-2' : ''}>
                <Field label="Actual passes">
                  <input
                    className="input"
                    value={actual.passes}
                    onChange={(e) => setA('passes', e.target.value)}
                    inputMode="numeric"
                    placeholder="8"
                  />
                </Field>
                {isStreet && (
                  <Field label="Actual miles" hint="street driving to/from">
                    <input
                      className="input"
                      value={actual.miles}
                      onChange={(e) => setA('miles', e.target.value)}
                      inputMode="numeric"
                      placeholder="40"
                    />
                  </Field>
                )}
              </div>
              {complete.isError && <div className="field-err">{errorMessage(complete.error)}</div>}
              <button className="record-btn" onClick={record} disabled={!actual.passes.trim() || busy}>
                {complete.isPending ? 'Recording…' : 'Record actuals & advance usage'}
              </button>
            </div>
          )
        )}

        {save.isError && !issue('name') && !issue('track') && !issue('date') && (
          <div className="field-err">{errorMessage(save.error)}</div>
        )}

        <button className="sheet-save-lg" onClick={submit} disabled={!valid || busy}>
          {save.isPending ? 'Saving…' : editing ? 'Save changes' : 'Add event'}
        </button>


        {editing && (
          <div className="danger-zone">
            {del.isError && <div className="field-err">{errorMessage(del.error)}</div>}
            {confirmDelete ? (
              <div className="danger-confirm">
                <span>Delete this event? Any service the forecast placed at it moves.</span>
                <div className="danger-actions">
                  <button className="act" onClick={() => setConfirmDelete(false)} disabled={busy}>
                    Keep
                  </button>
                  <button className="act act-danger" onClick={remove} disabled={busy}>
                    {del.isPending ? 'Deleting…' : 'Delete event'}
                  </button>
                </div>
              </div>
            ) : (
              <button className="danger-link" onClick={() => setConfirmDelete(true)} disabled={busy}>
                <Trash2 size={13} /> Delete this event
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
